import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to get current date string in local timezone
const getLocalDateString = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

// Calculate milliseconds until next midnight
const getMsUntilMidnight = () => {
  const now = new Date();
  const midnight = new Date(now);
  midnight.setHours(24, 0, 0, 0); // Next midnight
  return midnight.getTime() - now.getTime();
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [sessionDate, setSessionDate] = useState(localStorage.getItem('session_date'));

  // Logout function
  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('session_date');
    localStorage.removeItem('timezone');
    localStorage.removeItem('timezone_offset');
    setToken(null);
    setUser(null);
    setSessionDate(null);
  }, []);

  // Check if day has changed and handle session
  const checkDayChange = useCallback(() => {
    const currentDate = getLocalDateString();
    const storedDate = localStorage.getItem('session_date');
    
    if (storedDate && storedDate !== currentDate) {
      console.log(`[Session] Día cambió de ${storedDate} a ${currentDate}. Cerrando sesión...`);
      logout();
      // Show a message to the user
      window.location.href = '/login?session_expired=day_change';
      return true;
    }
    return false;
  }, [logout]);

  // Setup midnight timer
  useEffect(() => {
    if (!token || !user) return;

    // Check immediately on mount
    if (checkDayChange()) return;

    // Set timer for next midnight
    const msUntilMidnight = getMsUntilMidnight();
    console.log(`[Session] Próximo cierre de sesión en ${Math.round(msUntilMidnight / 1000 / 60)} minutos (medianoche)`);

    const midnightTimer = setTimeout(() => {
      console.log('[Session] ¡Medianoche! Cerrando sesión...');
      logout();
      window.location.href = '/login?session_expired=midnight';
    }, msUntilMidnight);

    // Also check every minute in case device was sleeping
    const intervalCheck = setInterval(() => {
      checkDayChange();
    }, 60000); // Check every minute

    return () => {
      clearTimeout(midnightTimer);
      clearInterval(intervalCheck);
    };
  }, [token, user, checkDayChange, logout]);

  // Fetch user on mount if token exists
  useEffect(() => {
    if (token) {
      // Check if day changed before fetching user
      const currentDate = getLocalDateString();
      const storedDate = localStorage.getItem('session_date');
      
      if (storedDate && storedDate !== currentDate) {
        // Day changed, force logout
        logout();
        setLoading(false);
        return;
      }
      
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Error fetching user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      // Get device timezone
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const timezoneOffset = new Date().getTimezoneOffset();
      const currentDate = getLocalDateString();
      
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password,
        timezone,
        timezone_offset: timezoneOffset
      });
      const { access_token, user: userData } = response.data;
      
      // Store session info
      localStorage.setItem('token', access_token);
      localStorage.setItem('timezone', timezone);
      localStorage.setItem('timezone_offset', timezoneOffset.toString());
      localStorage.setItem('session_date', currentDate);
      
      setToken(access_token);
      setUser(userData);
      setSessionDate(currentDate);
      
      console.log(`[Session] Sesión iniciada el ${currentDate}. Se cerrará a medianoche.`);
      
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Error al iniciar sesión'
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post(`${API}/auth/register`, userData);
      const data = response.data || {};
      const { access_token, user: newUser } = data;
      const currentDate = getLocalDateString();
      
      if (access_token) {
        localStorage.setItem('token', access_token);
        localStorage.setItem('session_date', currentDate);
        setToken(access_token);
        setSessionDate(currentDate);
      }
      
      if (newUser) {
        setUser(newUser);
      }
      
      return { success: true };
    } catch (error) {
      // Manejar errores de forma segura - el detail puede ser string, array u objeto
      let errorMessage = 'Error al registrar';
      const detail = error?.response?.data?.detail;
      
      if (detail) {
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail) && detail.length > 0) {
          // Pydantic validation errors
          errorMessage = detail.map(d => d.msg || 'Error de validación').join('. ');
        } else if (typeof detail === 'object' && detail.msg) {
          errorMessage = detail.msg;
        }
      }
      
      return {
        success: false,
        error: errorMessage
      };
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    fetchUser,
    sessionDate
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};